<template>
  <div class="fts-location">
    <div class="location-toggle">
      <font-awesome-icon
        :icon="['fas', 'map-marked-alt']"
        fixed-width
        class="location-map-marked"
        :class="{active: model}"
        @click.stop="openModal"
      />
      <fts-contenteditable
        text-wrap
        v-model="model.placeName"
        placeholder="Enter place name"
        class="location-name"
        v-if="validLocation"
      />
    </div>

    <fts-client-modal
      width="1080px"
      :title="item.displayName || 'Location'"
      ref="ftsClientModal"
      @closed="resetModal"
    >
      <!-- header:icon -->
      <i slot="icon" class="fts-icon-map-marker" />
      <!-- body -->
      <template #body>
        <div class="location-input-wrapper">
          <gmap-autocomplete
            class="location-input fts-form-control"
            placeholder="Search a location"
            @input="setPlaceName"
            @place_changed="setPlace"
            ref="locationInput"
          />
          <div class="location-input-action times-icon" v-if="placeName || validLocation">
            <font-awesome-icon :icon="['fas', 'times']" @click="clearMarker" />
          </div>
          <div class="location-input-action search-icon"  v-else>
            <font-awesome-icon :icon="['fas', 'search']" fixed-width />
          </div>
        </div>
        <gmap-map
          :center="center"
          :options="{
            mapTypeControl: false,
            streetViewControl: false,
            fullscreenControl: false,
          }"
          :zoom="16"
          class="vue-map-container"
          @click="mapClick($event)"
          ref="mymap"
        >
          <gmap-marker
            :clickable="true"
            :draggable="true"
            :position="marker.position"
            @click="center=marker.position"
          />
        </gmap-map>
      </template>
      <!-- footer:right -->
      <template #footerRight>
        <fts-btn text @click="closeModal">
          <font-awesome-icon :icon="['fas', 'times']" /> Close
        </fts-btn>
      </template>
    </fts-client-modal>
  </div>
</template>

<script>
import FTSClientModal from "./FTSClientModal";
import FTSContenteditable from "./FTSContenteditable";
import FTSBtn from "./FTSBtn";

export default {
  components: {
    "fts-client-modal": FTSClientModal,
    "fts-contenteditable": FTSContenteditable,
    "fts-btn": FTSBtn,
  },
  props: {
    item: Object,
    formData: Object,
  },
  data() {
    return {
      center: { lat: 1.3521, lng: 103.8198 },
      marker: {},
      placeName: null,
    };
  },
  methods: {
    setPlaceName(e) {
      // set placeName
      this.placeName = e.target.value;
      if (!this.placeName)
        this.clearMarker();
    },
    // receives a place object via the autocomplete component
    setPlace(place) {
      if (place && place.geometry && place.geometry.location) {
        this.setMarker(place.geometry.location);
        this.center = this.marker.position;
      }
      // set placeName
      this.placeName = this.$refs.locationInput.$el.value;
    },
    setMarker(location) {
      /* set marker */
      this.marker = { 
        position: {
          lat: location.lat(),
          lng: location.lng(),
        } 
      };
      /* panTo */
      // this.$refs.mymap.panTo(this.marker.position);
      /* set model */
      this.model = {
        lat: this.marker.position.lat,
        lng: this.marker.position.lng,
        placeName: this.$refs.locationInput.$el.value,
      }
    },
    clearMarker() {
      this.marker = {};
      this.model = null;
      this.placeName = null;
      if (this.$refs.locationInput && this.$refs.locationInput.$el)
        this.$refs.locationInput.$el.value = null;
    },
    mapClick(event) {
      // debug("mapClickEvent", event);
      this.setMarker(event.latLng);
    },
    geolocate: function() {
      if (this.validLocation) {
        const location = {
          lat: this.model && this.model.lat,
          lng: this.model && this.model.lng,
        };
        this.center = location;
        this.marker = { position: location };
      } else {
        navigator.geolocation.getCurrentPosition(position => {
          this.center = {
            lat: position.coords.latitude,
            lng: position.coords.longitude
          };
        });
      }
    },
    /* modal*/
    openModal() {
      this.geolocate();
      this.$refs.ftsClientModal.show();
      this.$nextTick(() => {
        this.$refs.locationInput.$el.focus();
        if (this.model && this.model.placeName) {
          this.$refs.locationInput.$el.value = this.model.placeName;
          this.placeName = this.model.placeName;
        }
      });
    },
    closeModal() {
      this.$refs.ftsClientModal.close();
    },
    resetModal() {
      if (this.$refs.locationInput.$el.value && this.validLocation)
        this.model = {
          ...this.model,
          placeName: this.$refs.locationInput.$el.value,
        }
      this.placeName = null;
      this.$emit('close');
    },
  },
  computed: {
    model: {
      get() {
        return this.formData[this.item.fieldName];
      },
      set(val) {
        if (val)
          this.$set(this.formData, this.item.fieldName, val);
        else
          this.$delete(this.formData, this.item.fieldName);
      }
    },
    validLocation() {
      return !!(this.model && this.model.lat && this.model.lng);
    },
  },
};
</script>

<style lang="scss">
@import "./src/assets/scss/vars.scss";

.fts-location {
  .location-toggle {
    display: flex;
    align-items: start;
    width: 100%;
    min-height: $inputHeight;
    padding: .375rem .75rem;
    line-height: 1.5;  
    .location-name {
      margin-left: .575rem;
      font-size: .75rem;
    }
    svg {
      &.active {
        color: $primaryColor;
      }
      margin-top: 3px;
      font-size: .875rem;
      color: var(--fts-gray-5);
      cursor: pointer;
    }
  }
  .location-input-wrapper {
    position: relative;
    margin-bottom: .75rem;
    .location-input {
      padding-right: 2rem;
    }
    .location-input-action {
      @include absolute-center-y;
      font-size: .75rem;
      right: .75rem;
      svg {
        color: var(--fts-gray-5);
      }
      &.search-icon {
        @include pointer-events-none;
      }
      &.times-icon {
        cursor: pointer;
      }
    }
  }
  .vue-map-container {
    width:100%;
    height: 400px;
  }
}
</style>
